import { ButtonAction } from '@heidi/analytics/vocabulary';
import { TextMenuButton, type TextMenuButtonProps } from '@heidi/editor/ui';
import { useTranslations } from '@heidi/runtime/i18n';
import { type Editor } from '@tiptap/react';
import {
  BetweenHorizontalStart,
  Bold,
  Book,
  Code,
  Copy,
  Italic,
  List,
  ListOrdered,
  Replace,
  Underline,
  type LucideIcon,
} from 'lucide-react';
import { useCallback } from 'react';
import { useIntl } from 'react-intl';

import { useTrackMenuClick } from './use-track-menu-click';

/**
 * The app wiring layer over the packaged `TextMenuButton` primitive (6.5/6.6
 * wave B-2 — the 6.1a shell pattern). The primitive owns icon + tooltip +
 * shortcut-display + active/click mechanics; this file keeps everything
 * product-shaped exactly where it was: tooltip translation, the
 * `button_clicked` analytics with its editor-variant stamping and the
 * ConvertToSection/Copy double-count opt-outs, action enums, and the
 * editor-button focus-restore composition. All ten exports keep their
 * signatures, so all six consumers (four editor twins, calls, workflows) are
 * untouched.
 */

interface ButtonProps {
  iconSize?: number;
  className?: string;
  'data-testid'?: string;
  /** Toolbar button size; defaults to the Toolbar.Button default ('xs'). */
  size?: TextMenuButtonProps['size'];
}

interface TextMenuButtonConfig {
  tooltipKey: string;
  icon: LucideIcon;
  shortcutKey?: string;
  /** button_clicked action fired on click. */
  action: ButtonAction;
  /** Stable button_clicked label (locale-independent). */
  label: string;
}

/**
 * Default testid derived from the locale-independent analytics label, so
 * selectors never couple to tooltip copy (`bullet_list` → `text-menu-bullet-list`).
 */
const toTestId = (label: string) => `text-menu-${label.replace(/_/g, '-')}`;

const createFormattingButton = ({
  tooltipKey,
  icon,
  shortcutKey,
  action,
  label,
}: TextMenuButtonConfig) => {
  const FormattingButton = ({
    onClick,
    active,
    disabled,
    disabledTooltip,
    iconSize,
    className,
    size,
    'data-testid': testId,
  }: {
    onClick: () => void;
    active: boolean;
    disabled?: boolean;
    disabledTooltip?: string;
  } & ButtonProps) => {
    const { t } = useTranslations();
    const trackClick = useTrackMenuClick();
    const handleClick = useCallback(() => {
      trackClick(action, label);
      onClick();
    }, [onClick, trackClick]);

    return (
      <TextMenuButton
        icon={icon}
        tooltip={t(tooltipKey as Parameters<typeof t>[0])}
        shortcutKey={shortcutKey}
        onClick={handleClick}
        active={active}
        disabled={disabled}
        iconSize={iconSize}
        disabledTooltip={disabledTooltip}
        className={className}
        size={size}
        data-testid={testId ?? toTestId(label)}
      />
    );
  };

  FormattingButton.displayName = `FormattingButton(${icon?.displayName || icon?.name || 'Icon'})`;
  return FormattingButton;
};

const createEditorButton = ({
  tooltipKey,
  icon,
  action,
  label,
}: Omit<TextMenuButtonConfig, 'shortcutKey'>) => {
  const EditorButton = ({
    onClick,
    editor,
    iconSize,
    className,
    size,
    'data-testid': testId,
  }: {
    onClick: () => void;
    editor: Editor;
  } & ButtonProps) => {
    const { t } = useTranslations();
    const trackClick = useTrackMenuClick();
    const handleClick = useCallback(() => {
      trackClick(action, label);
      onClick();
      editor.commands.focus(editor.state.selection.from);
    }, [editor, onClick, trackClick]);

    return (
      <TextMenuButton
        icon={icon}
        tooltip={t(tooltipKey as Parameters<typeof t>[0])}
        onClick={handleClick}
        iconSize={iconSize}
        className={className}
        size={size}
        data-testid={testId ?? toTestId(label)}
      />
    );
  };

  EditorButton.displayName = `EditorButton(${icon?.displayName || icon?.name || 'Icon'})`;
  return EditorButton;
};

export const BoldButton = createFormattingButton({
  tooltipKey: 'tooltips.bold',
  icon: Bold,
  shortcutKey: 'B',
  action: ButtonAction.TextMenuBold,
  label: 'bold',
});

export const ItalicButton = createFormattingButton({
  tooltipKey: 'tooltips.italic',
  icon: Italic,
  shortcutKey: 'I',
  action: ButtonAction.TextMenuItalic,
  label: 'italic',
});

export const UnderlineButton = createFormattingButton({
  tooltipKey: 'tooltips.underline',
  icon: Underline,
  shortcutKey: 'U',
  action: ButtonAction.TextMenuUnderline,
  label: 'underline',
});

export const BulletListButton = createFormattingButton({
  tooltipKey: 'tooltips.unorderedList',
  icon: List,
  action: ButtonAction.TextMenuBulletList,
  label: 'bullet_list',
});

export const CodeBlockButton = ({
  onClick,
  active,
  iconSize,
  className,
  size,
  'data-testid': testId,
}: {
  onClick: () => void;
  active: boolean;
} & ButtonProps) => {
  const intl = useIntl();
  const trackClick = useTrackMenuClick();
  const handleClick = useCallback(() => {
    trackClick(ButtonAction.TextMenuCodeBlock, 'code_block');
    onClick();
  }, [onClick, trackClick]);

  return (
    <TextMenuButton
      icon={Code}
      tooltip={intl.formatMessage({
        defaultMessage: 'Code block',
        id: '8typ4gfVt/',
      })}
      onClick={handleClick}
      active={active}
      iconSize={iconSize}
      className={className}
      size={size}
      data-testid={testId ?? toTestId('code_block')}
    />
  );
};

// Opt out of the generic button_clicked: converting to a section already fires
// the semantic `section_created` event, so tracking here would double count.
export const ConvertToSectionButton = ({
  onClick,
  iconSize,
  className,
  size,
}: {
  onClick: () => void;
} & ButtonProps) => {
  const intl = useIntl();

  return (
    <TextMenuButton
      icon={BetweenHorizontalStart}
      tooltip={intl.formatMessage({
        defaultMessage: 'Convert to section',
        id: 'oGhLDAJwnw',
      })}
      onClick={onClick}
      iconSize={iconSize}
      className={className}
      size={size}
      data-testid="text-menu-convert-to-section"
    />
  );
};

export const OrderedListButton = createFormattingButton({
  tooltipKey: 'tooltips.orderedList',
  icon: ListOrdered,
  action: ButtonAction.TextMenuOrderedList,
  label: 'ordered_list',
});

// Opt out of the generic button_clicked: copy already fires the semantic
// `session_copied` event, so tracking here would double count.
export const CopyButton = ({
  onClick,
  iconSize,
  className,
  size,
  'data-testid': testId,
}: {
  onClick: () => Promise<void>;
} & ButtonProps) => {
  const { t } = useTranslations();

  return (
    <TextMenuButton
      icon={Copy}
      tooltip={t('tooltips.copy')}
      shortcutKey="C"
      onClick={() => void onClick()}
      iconSize={iconSize}
      className={className}
      size={size}
      data-testid={testId ?? toTestId('copy')}
    />
  );
};

export const FindReplaceButton = createEditorButton({
  tooltipKey: 'tooltips.findReplace',
  icon: Replace,
  action: ButtonAction.TextMenuFindReplace,
  label: 'find_replace',
});

export const SnippetButton = createEditorButton({
  tooltipKey: 'tooltips.createSnippet',
  icon: Book,
  action: ButtonAction.TextMenuCreateSnippet,
  label: 'create_snippet',
});
